import React from "react";

export interface ResizableSplit {
  leftFraction: number;
  isDragging: boolean;
  handleResizeStart: (e: React.MouseEvent) => void;
}

/**
 * Mouse-drag resizable two-column split. Returns the left column fraction
 * (clamped to [min, max]) and a handler to start dragging.
 *
 * The container element is located via `containerSelector` so the hook does
 * not need a ref forwarded across components.
 */
export function useResizableSplit(
  containerSelector: string,
  initialFraction = 2 / 3,
  min = 0.25,
  max = 0.75
): ResizableSplit {
  const [leftFraction, setLeftFraction] = React.useState(initialFraction);
  const [isDragging, setIsDragging] = React.useState(false);

  const handleResizeStart = React.useCallback((e: React.MouseEvent) => {
    e.preventDefault();
    setIsDragging(true);
  }, []);

  React.useEffect(() => {
    if (!isDragging) return;

    const onMove = (e: MouseEvent) => {
      const container = document.querySelector(containerSelector);
      if (!container) return;
      const rect = container.getBoundingClientRect();
      const x = e.clientX - rect.left;
      const fraction = Math.max(min, Math.min(max, x / rect.width));
      setLeftFraction(fraction);
    };

    const onUp = () => setIsDragging(false);

    window.addEventListener("mousemove", onMove);
    window.addEventListener("mouseup", onUp);
    return () => {
      window.removeEventListener("mousemove", onMove);
      window.removeEventListener("mouseup", onUp);
    };
  }, [isDragging, containerSelector, min, max]);

  return { leftFraction, isDragging, handleResizeStart };
}
